import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Card, Loading, EmptyState } from "../components/UI";
import { formatApiError } from "../lib/format";
import { Upload, User, Shield, CheckCircle2, Image as ImgIcon, AlertTriangle, UsersRound, Plus, Trash2 } from "lucide-react";

export default function Onboarding() {
  const [profile, setProfile] = useState(null);
  const [guarantor, setGuarantor] = useState(null);
  const [occupants, setOccupants] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/tenants/me");
      setProfile(r.data);
      setGuarantor(r.data.guarantor || null);
      const occ = await api.get(`/tenants/${r.data.id}/occupants`).catch(() => ({ data: [] }));
      setOccupants(occ.data);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError("Your tenant profile has not been created yet. Please contact your property manager.");
      } else if (status === 401 || status === 403) {
        setError("You need to be logged in as a tenant to view this page.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      tenant_address: f.get("address"),
      occupation_or_business: f.get("occupation"),
      use_of_apartment: f.get("use"),
      id_type: f.get("id_type"),
      emergency_contact_name: f.get("ec_name"),
      emergency_contact_phone: f.get("ec_phone"),
      occupant_count: Number(f.get("occupants") || 1),
      phone: f.get("phone"),
    };
    try {
      await api.patch(`/tenants/${profile.id}`, payload);
      toast.success("Profile saved");
      await reload();
      setStep(2);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadFile = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", `tenant/${profile.id}`);
      fd.append("tenant_id", profile.id);
      fd.append("document_type", field);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/tenants/${profile.id}`, { [field]: data.file_url });
      toast.success("File uploaded");
      await reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setUploading(false);
      // Reset the input so the same file can be re-selected if needed
      try { e.target.value = ""; } catch {}
    }
  };

  const saveGuarantor = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post(`/tenants/${profile.id}/guarantor`, {
        guarantor_name: f.get("name"),
        guarantor_address: f.get("address"),
        guarantor_occupation: f.get("occupation"),
        guarantor_phone: f.get("phone"),
        guarantor_email: f.get("email"),
        id_type: f.get("id_type"),
      });
      toast.success("Guarantor saved");
      await reload();
      setStep(3);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addOccupant = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post(`/tenants/${profile.id}/occupants`, {
        full_name: f.get("full_name"),
        relationship: f.get("relationship"),
        phone: f.get("phone") || "",
        age_category: f.get("age_category"),
      });
      toast.success("Occupant added");
      e.target.reset();
      await reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeOccupant = async (id) => {
    if (!confirm("Remove this occupant?")) return;
    try { await api.delete(`/occupants/${id}`); toast.success("Removed"); await reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  if (error || !profile) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Profile not available"
        description={error || "Your tenant profile could not be loaded."}
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="fade-up">
        <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Tenant Onboarding</h1>
        <p className="text-[#5E7166] text-sm">Complete your profile to get your lease generated.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { n: 1, label: "Profile", icon: User },
          { n: 2, label: "Guarantor", icon: Shield },
          { n: 3, label: "Occupants", icon: UsersRound },
          { n: 4, label: "Done", icon: CheckCircle2 },
        ].map((s, i) => (
          <React.Fragment key={s.n}>
            <button type="button" onClick={() => setStep(s.n)} className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm transition-colors ${step === s.n ? "bg-[#0E5C3A] text-white" : "bg-white border border-[#E5EBE8] text-[#5E7166] hover:border-[#C9A227]"}`} data-testid={`step-${s.n}`}>
              <s.icon size={14}/> <span className="hidden sm:inline">{s.label}</span>
            </button>
            {i < 3 && <div className="flex-1 h-px bg-[#E5EBE8]"></div>}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <Card title="Step 1 — Personal Profile">
          <form onSubmit={saveProfile} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full Name (read-only)" value={profile.full_name || ""} readOnly/>
              <Field label="Email" value={profile.email || ""} readOnly/>
              <Field label="Phone" name="phone" defaultValue={profile.phone || ""}/>
              <Field label="Occupants" name="occupants" type="number" defaultValue={profile.occupant_count || 1}/>
              <div className="sm:col-span-2"><Field label="Current Address" name="address" defaultValue={profile.tenant_address || ""}/></div>
              <Field label="Occupation / Business" name="occupation" defaultValue={profile.occupation_or_business || ""}/>
              <Field label="Use of Apartment" name="use" defaultValue={profile.use_of_apartment || ""} placeholder="Residential / Pharmacy etc."/>
              <Field label="ID Type" name="id_type" defaultValue={profile.id_type || ""} placeholder="NIN / Driver's License / Passport"/>
              <Field label="Emergency Contact Name" name="ec_name" defaultValue={profile.emergency_contact_name || ""}/>
              <Field label="Emergency Contact Phone" name="ec_phone" defaultValue={profile.emergency_contact_phone || ""}/>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-[#E5EBE8]">
              <FileField label="Passport Photo" current={profile.passport_photo_url} onChange={(e) => uploadFile(e, "passport_photo_url")} uploading={uploading} testid="upload-passport"/>
              <FileField label="ID Document" current={profile.id_document_url} onChange={(e) => uploadFile(e, "id_document_url")} uploading={uploading} testid="upload-id"/>
            </div>

            <button type="submit" className="btn-gold w-full justify-center" data-testid="save-profile-btn">Save & Continue</button>
          </form>
        </Card>
      )}

      {step === 2 && (
        <Card title="Step 2 — Guarantor Details">
          <form onSubmit={saveGuarantor} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full Name" name="name" defaultValue={guarantor?.guarantor_name || ""} required/>
              <Field label="Phone" name="phone" defaultValue={guarantor?.guarantor_phone || ""} required/>
              <Field label="Email" name="email" type="email" defaultValue={guarantor?.guarantor_email || ""}/>
              <Field label="Occupation" name="occupation" defaultValue={guarantor?.guarantor_occupation || ""}/>
              <div className="sm:col-span-2"><Field label="Address" name="address" defaultValue={guarantor?.guarantor_address || ""} required/></div>
              <Field label="ID Type" name="id_type" defaultValue={guarantor?.id_type || ""} placeholder="NIN / Driver's License"/>
            </div>
            <button type="submit" className="btn-gold w-full justify-center" data-testid="save-guarantor-btn">Save Guarantor</button>
          </form>
        </Card>
      )}

      {step === 3 && (
        <Card title="Step 3 — Occupants (optional)">
          <p className="text-sm text-[#5E7166] mb-4">List anyone else living with you at this unit. Helps with security & emergency contacts.</p>
          <ul className="space-y-2 mb-4">
            {occupants.length === 0 ? <li className="text-sm text-[#5E7166] italic">No occupants added yet.</li> :
              occupants.map(o => (
                <li key={o.id} className="flex items-center justify-between border border-[#E5EBE8] rounded-lg p-3" data-testid={`occupant-${o.id}`}>
                  <div>
                    <div className="font-semibold text-sm">{o.full_name} <span className="text-xs text-[#5E7166]">({o.age_category})</span></div>
                    <div className="text-xs text-[#5E7166]">{o.relationship}{o.phone ? ` · ${o.phone}` : ""}</div>
                  </div>
                  <button type="button" onClick={() => removeOccupant(o.id)} className="text-[#C92A2A] hover:bg-[#FEF0F0] p-2 rounded"><Trash2 size={14}/></button>
                </li>
              ))}
          </ul>
          <form onSubmit={addOccupant} className="space-y-3 border-t border-[#E5EBE8] pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full Name" name="full_name" required/>
              <Field label="Relationship" name="relationship" placeholder="Spouse / Child / Sibling / Friend" required/>
              <Field label="Phone (optional)" name="phone"/>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Age Category</span>
                <select name="age_category" defaultValue="Adult" className="input-modav mt-1" data-testid="field-age-category">
                  <option value="Adult">Adult</option><option value="Child">Child</option>
                </select>
              </label>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-gold flex-1 justify-center" data-testid="add-occupant-btn"><Plus size={14}/> Add Occupant</button>
              <button type="button" onClick={() => setStep(4)} className="btn-outline-emerald">Skip & Finish</button>
            </div>
          </form>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto bg-[#E8F5EE] text-[#0E5C3A] rounded-full flex items-center justify-center mb-4"><CheckCircle2 size={28}/></div>
            <h2 className="font-serif text-2xl font-bold">Onboarding complete!</h2>
            <p className="text-[#5E7166] mt-2">Your manager will review your details and generate your lease shortly.</p>
            <button type="button" onClick={() => setStep(1)} className="btn-outline-emerald mt-4 mx-auto">Review my details</button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, name, type = "text", defaultValue, value, readOnly, required, placeholder }) {
  const testid = `field-${name || (label || "").toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">{label}</span>
      <input
        name={name}
        type={type}
        defaultValue={readOnly ? undefined : defaultValue}
        value={readOnly ? value : undefined}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder}
        className="input-modav mt-1"
        data-testid={testid}
      />
    </label>
  );
}

function FileField({ label, current, onChange, uploading, testid }) {
  // Use a <label> wrapper so the whole drop-zone triggers the file picker
  // — no absolute-positioned overlay needed.
  return (
    <div>
      <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">{label}</span>
      <label className={`mt-1 flex flex-col items-center justify-center border-2 border-dashed border-[#E5EBE8] rounded-lg p-4 text-center cursor-pointer hover:border-[#C9A227] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
        {current ? (
          <div className="flex items-center justify-center gap-2 text-sm text-[#0E5C3A]"><CheckCircle2 size={16}/> Uploaded</div>
        ) : (
          <div className="text-xs text-[#5E7166] flex flex-col items-center"><ImgIcon size={20} className="mb-1"/> Click to upload</div>
        )}
        <span className="btn-outline-emerald text-xs mt-2 pointer-events-none">
          <Upload size={12}/> {uploading ? "Uploading…" : (current ? "Replace" : "Upload")}
        </span>
        <input
          type="file"
          accept="image/*,application/pdf"
          onChange={onChange}
          disabled={uploading}
          className="sr-only"
          data-testid={testid}
        />
      </label>
    </div>
  );
}
